import type { PaymentStatus } from '@/constants/institute';
import { todayISO } from '@/features/institute/calendar/utils/dates';
import { googleMapsSearchUrl } from '@/lib/utils';
import type { Institute } from '@/types/institute';

/**
 * Helpers the customer card and the customer record both use, so the two
 * screens cannot disagree about where a school is or whether it owes us.
 */

/** Google Maps for the institute's full street address. */
export function instituteMapsUrl(
  institute: Pick<
    Institute,
    'addressLine1' | 'locality' | 'city' | 'state' | 'pincode' | 'latitude' | 'longitude'
  >,
): string {
  // A dropped pin is the campus gate; an address search is wherever the map
  // thinks the locality is. The pin wins whenever there is one.
  if (institute.latitude !== null && institute.longitude !== null) {
    return googleMapsSearchUrl([`${institute.latitude},${institute.longitude}`]);
  }
  return googleMapsSearchUrl([
    institute.addressLine1,
    institute.locality,
    institute.city,
    institute.state,
    institute.pincode,
  ]);
}

/**
 * Whether the licence fee is past its due date and still unpaid.
 *
 * Only while unpaid: a fee received after its date is settled, not late.
 * Compared as `YYYY-MM-DD` strings in the reader's own day, so a fee due today
 * is not overdue until tomorrow.
 */
export function isSubscriptionOverdue(dueDate: string | null, paymentStatus: PaymentStatus): boolean {
  return dueDate !== null && paymentStatus === 'UNPAID' && dueDate.slice(0, 10) < todayISO();
}
